import { Geometry } from 'geojson';

import { ITASCA_URL } from '@/constants/urls';
import { OpenSearchParcel } from '@/endpoints/itasca/parcels/search';
import { resolveV3 } from '@/endpoints/resolveV3';
import DataResponse from '@/endpoints/types';
import type { StateFips } from '@/utils/states';

import {
  AbsenteeLandownerOptions,
  LandUseCategory,
  LeadListBackendDataType,
} from './typesAndConstants';

type SharedPayloadOptions = {
  name?: string;
  list_type?: LeadListBackendDataType;
};

type SavedSelectionPayload = SharedPayloadOptions & {
  saved_selection__id: number;
};

type ClipsPayload = SharedPayloadOptions & {
  clips: OpenSearchParcel['clip'][];
};

export type LeadListSoilFilterType =
  | 'nccpi'
  | 'csr2'
  | 'pi'
  | 'wapi'
  | 'cpi'
  | 'storie';

type FiltersPayload = SharedPayloadOptions & {
  filter: {
    state_fips?: string;
    county_fips?: string[];
    geometry?: Geometry;
    min_acres?: number;
    max_acres?: number;
    absentee_landowners?: AbsenteeLandownerOptions;
    land_use_categories?: LandUseCategory[];
    min_assessed_improvement_value?: number;
    max_assessed_improvement_value?: number;
    assessed_improvement_value_operation?: 'include' | 'exclude';
    water_districts?: string[];
    max_sale_date?: string;
    min_sale_date?: string;
    crop?: {
      id: number;
      year: number;
    };
    soil?: {
      type: LeadListSoilFilterType;
      min_score?: number;
      max_score?: number;
    };
  };
};

export type CreateLeadListPayload =
  | SavedSelectionPayload
  | ClipsPayload
  | FiltersPayload;

export type LeadList = {
  id: number;
  record_count: number;
  cost: number;
  /** This is only populated after the mailing list has been purchased */
  csv?: string;
  created_at: number;
  purchased_at: number;
  name: string;
  state_fips: StateFips;
  county_fips: string[];
  clips?: OpenSearchParcel['clip'][];
  geometry: Geometry;
  min_acres: number;
  max_acres: number;
  absentee_landowners: AbsenteeLandownerOptions;
  land_use_categories: LandUseCategory[];
  min_assessed_improvement_value: number | null;
  max_assessed_improvement_value: number | null;
  assessed_improvement_value_operation: 'include' | 'exclude' | null;
  water_districts: string[] | null;
  list_type: LeadListBackendDataType;
  crop_id: number | null;
  crop_year: number | null;
  soil_type: LeadListSoilFilterType | null;
  max_soil_score: number | null;
  min_soil_score: number | null;
  min_sale_date: string | null;
  max_sale_date: string | null;
};

export type LeadListPreview = {
  'Total Acres': number;
  'Total Number of Parcels': number;
  'Mailing Address': string;
  'Street Address': string;
  'Mail City': string;
  'Mail State': string;
  'Mail Zip': string;
  'Addressee 1': string;
  'Addressee 2': string;
  'Addressee 3': string | null;
  'Addressee 4': string | null;
};

export type CreatedLeadListEndpointResponse = LeadList & {
  preview: LeadListPreview;
  state_name: string | null;
  county_names: string[] | null;
  geometry_acres: number | null;
  user: {
    id: number;
    email: string;
    name: string;
  };
};

export async function createLeadList(
  data: CreateLeadListPayload,
  signal?: AbortSignal
): Promise<DataResponse<CreatedLeadListEndpointResponse>> {
  return resolveV3(
    {
      data,
      method: 'POST',
      signal,
      timeout: 60000, // 1 minute timeout
      url: `${ITASCA_URL}/mailing-lists/`,
    },
    {
      baseUrl: 'itasca',
    }
  );
}
